import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { isArray } from 'util';
import { DialogComponent, iDialogServiceData } from './dialog/dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackBarComponent } from './snack-bar/snack-bar.component';

@Injectable({
  providedIn: 'root'
})
export class DialogService {
  constructor(private dialog: MatDialog, private MatSnackBar: MatSnackBar) {}

  public alert(
    text: string,
    content?: string,
    buttonLabel: string = 'אישור',
    dialogRef?: (dialogRef: MatDialogRef<DialogComponent>) => void
  ): Promise<void> {
    const dialog = this.showDialog({
      text,
      content,
      buttonLabel
    });

    if (dialogRef) dialogRef(dialog);

    return this.getPromise<void>(dialog).then(r => null);
  }

  public confirm(
    text: string,
    content?: string,
    buttonLabel: string = 'אישור',
    cancelLabel: string = 'ביטול',
    dialogRef?: (dialogRef: MatDialogRef<DialogComponent>) => void
  ): Promise<boolean> {
    const dialog = this.showDialog({
      text,
      content,
      buttonLabel,
      cancelLabel
    });

    if (dialogRef) dialogRef(dialog);

    return this.getPromise<boolean>(dialog).then(r => r == true);
  }

  public prompt<T extends iDialogServiceInput[] | iDialogServiceInput | string>(
    text: string,
    input: T,
    content?: string,
    buttonLabel: string = 'אישור',
    cancelLabel: string = 'ביטול',
    dialogRef?: (dialogRef: MatDialogRef<DialogComponent>) => void
  ): Promise<InputReturnType<T extends string ? InputTypeText[] : T extends any[] ? T : T[]> | false> {
    let inputs: iDialogServiceInput[];
    if (typeof input == 'string')
      inputs = [
        {
          type: 'text',
          label: input,
          required: true
        }
      ];
    else if (!Array.isArray(input)) inputs = [input as iDialogServiceInput];
    else inputs = input as iDialogServiceInput[];

    inputs.forEach(i => {
      if (i.type !== 'select') return;
      i.enum = (i.enum as string[]).map(e => {
        if (Array.isArray(e) && e.length == 2) return e;
        return [e.toString(), e.toString()];
      });
    });

    const dialog = this.showDialog({
      text,
      content,
      buttonLabel,
      cancelLabel,
      inputs
    });

    if (dialogRef) dialogRef(dialog);

    return this.getPromise(dialog);
  }

  public snackBar(
    text: string,
    content?: string,
    data: {
      buttonLabel?: string;
      timeInMs?: number;
      vibrate?: boolean;
    } = {}
  ) {
    data = {
      timeInMs: 10000,
      vibrate: true,
      ...data
    };

    return this.MatSnackBar.openFromComponent(SnackBarComponent, {
      verticalPosition: 'bottom',
      announcementMessage: `${text} ${content}`.trim(),
      data: {
        text,
        content,
        buttonLabel: data.buttonLabel
      },
      direction: 'rtl',
      duration: data.timeInMs,
      horizontalPosition: 'center'
    })
      .onAction()
      .toPromise();
  }

  private showDialog(data: iDialogServiceData): MatDialogRef<DialogComponent> {
    return this.dialog.open(DialogComponent, { data });
  }

  private getPromise<T>(dialog: MatDialogRef<DialogComponent>): Promise<T> {
    return dialog.afterClosed().toPromise();
  }
}

export type iDialogServiceInput =
  | InputTypeText
  | InputTypeTextarea
  | InputTypeSelect
  | InputTypeColorpicker
  | InputTypeAutocomplete
  | InputTypeDate
  | InputTypeTime
  | InputTypeMonth;

interface InputTypeText {
  type: 'textarea' | 'text' | 'password' | 'url' | 'number' | 'email';
  readonly?: false;
  label: string;
  required: boolean;
  value?: string;
  direction?: 'ltr' | 'rtl';
}
interface InputTypeTextarea {
  type: 'textarea' | 'text';
  readonly: true;
  label: string;
  value: string;
  direction?: 'ltr' | 'rtl';
}
interface InputTypeSelect {
  type: 'select';
  label: string;
  required: boolean;
  value?: string;
  enum: string[] | string[][];
}
interface InputTypeColorpicker {
  type: 'colorpicker';
  label: string;
  value?: string;
  opacity: boolean;
}
interface InputTypeAutocomplete {
  type: 'autocomplete';
  label: string;
  value?: string;
  enum: string[];
  required: boolean;
}
interface InputTypeDate {
  type: 'date';
  label: string;
  required: boolean;
  value?: Date;
  min: Date;
  max: Date;
}
interface InputTypeTime {
  type: 'time';
  required: boolean;
  value?: { h: number; m: number };
}
interface InputTypeMonth {
  type: 'month';
  label: string;
  required: boolean;
  value?: Date;
  min: Date;
  max: Date;
}

type InputReturnType<T extends iDialogServiceInput[]> = (T[keyof T] extends infer V
  ? V extends InputTypeText
    ? InputTypeText & { value: InputTypeText['value'] }
    : V extends InputTypeTextarea
    ? InputTypeTextarea & { value: InputTypeTextarea['value'] }
    : V extends InputTypeSelect
    ? InputTypeSelect & { value: InputTypeSelect['value'] }
    : V extends InputTypeColorpicker
    ? InputTypeColorpicker & { value: InputTypeColorpicker['value'] }
    : V extends InputTypeAutocomplete
    ? InputTypeAutocomplete & { value: InputTypeAutocomplete['value'] }
    : V extends InputTypeDate
    ? InputTypeDate & { value: InputTypeDate['value'] }
    : V extends InputTypeTime
    ? InputTypeTime & { value: InputTypeTime['value'] }
    : V extends InputTypeMonth
    ? InputTypeMonth & { value: InputTypeMonth['value'] }
    : never
  : never)[];
